import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { CheckmarkRegular, ChevronDownRegular, QuestionRegular } from '@fluentui/react-icons';
import { UserListSorting, getSortedUserLabel } from '@mnspro-cloud/services';
import {
  AbsenceReasonCategory,
  AbsenceReasonResponse,
  AbsenceResponse,
  AbsenceState,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import cn from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeMode, useProviderTheme } from '../../../hooks';
import { Loader } from '../../../Loader/Loader';
import { Tag, TagColorEnum } from '../../../Tags/Tags';
import { getAbsenceTagColor } from '../../../Absences/common/getAbsenceTagColor';
import { StudentAbsences } from './AbsenceEditDialog.interfaces';
import { getAbsenceIcon } from './AbsencesEditDialog.utils';
import './StudentAbsenceButton.scss';
import { getAbsenceReasonText } from './StudentAbsenceButton.utils';

export interface StudentAbsenceButtonProps {
  disabled: boolean;
  studentAbsences: StudentAbsences;
  absenceReasons: AbsenceReasonResponse[];
  isUpdating?: boolean;
  absenceReasonSelected: (
    absenceReasonResponse: AbsenceReasonResponse | null,
    absenceReason: AbsenceResponse,
  ) => void;
  preferenceSorting?: UserListSorting;
}

interface MenuOption {
  content: string;
  key: string;
  item: AbsenceReasonResponse | null;
}

export const StudentAbsenceButton: FC<StudentAbsenceButtonProps> = ({
  disabled,
  studentAbsences,
  isUpdating,
  absenceReasons,
  absenceReasonSelected,
  preferenceSorting,
}) => {
  const { t } = useTranslation();
  const { absences, student } = studentAbsences;
  const theme = useProviderTheme();

  const absence = absences.find((a) => a.state === AbsenceState.absent) ?? absences[0];

  const menuOptions: MenuOption[] = absenceReasons.map((reason) => ({
    content: reason.shortName,
    key: reason.id,
    item: reason,
  }));
  menuOptions.unshift({
    content: t('classbook:absences:absencereason_present', { defaultValue: 'Anwesend' }),
    key: 'present-id',
    item: null,
  });

  const handleMenuItemClick = (option: MenuOption) => {
    const absenceReason = option.item;
    let absenceToUpdate = absence;

    if (absenceReason && absences.length > 1) {
      //Left Early AND late
      const reasonMatchingAbsence = absences.find(
        (absence) => absence.absenceReason?.category === absenceReason.category,
      );

      if (reasonMatchingAbsence) {
        //Update time of existing absence
        absenceToUpdate = reasonMatchingAbsence;
      }
    }

    absenceReasonSelected(absenceReason, absenceToUpdate);
  };

  const getIcon = () => {
    let icon: JSX.Element | null = null;

    if (absence && absence.state === AbsenceState.unchecked) {
      return <QuestionRegular className="absenceStatusIcon iconGrey" />;
    }

    if (absence.absenceReason === null && absence.state !== AbsenceState.present) {
      icon = getAbsenceIcon(AbsenceReasonCategory.none);
    } else if (absence.absenceReason === null && absence.state === AbsenceState.present) {
      icon = <CheckmarkRegular className="absenceStatusIcon iconGreen" />;
    } else if (absence.absenceReason !== null) {
      icon = getAbsenceIcon(absence.absenceReason?.category ?? AbsenceReasonCategory.none);
    }

    return icon;
  };

  const getBadges = () => {
    const firstAbsence = absence;
    const absenceReasonCategory = firstAbsence?.absenceReason?.category;
    const badgesToDisplay: JSX.Element[] = [];
    if (
      absenceReasonCategory === AbsenceReasonCategory.late ||
      absenceReasonCategory === AbsenceReasonCategory.leftEarly
    ) {
      for (const absence of absences) {
        const offset = absence.offset;
        if (offset && offset !== '00:00:00') {
          badgesToDisplay.push(
            <Tag
              key={
                absence.id ?? `${absence.affectedStudent.id}\\${absence.absenceReason?.category}`
              }
              color={getAbsenceTagColor(absence)}
              label={getAbsenceReasonText(absence)}
            />,
          );
        }
      }
    } else {
      badgesToDisplay.push(
        <Tag
          key={
            firstAbsence.id ??
            `${firstAbsence.affectedStudent.id}\\${firstAbsence.absenceReason?.category}`
          }
          color={getAbsenceTagColor(firstAbsence)}
          label={getAbsenceReasonText(firstAbsence)}
        />,
      );
    }

    if (
      !firstAbsence.absenceReason?.includeInStatistics &&
      firstAbsence.state !== AbsenceState.present &&
      firstAbsence.state !== AbsenceState.unchecked
    ) {
      badgesToDisplay.push(
        <Tag
          key={
            firstAbsence.id ??
            `${firstAbsence.affectedStudent.id}\\${firstAbsence.absenceReason?.category}_notInluded`
          }
          color={TagColorEnum.Yellow}
          label={t('classbook:absences:absencereason_notincluded_statistic', {
            defaultValue: 'Stat. n. relev.',
          })}
        />,
      );
    }

    return badgesToDisplay;
  };

  const trigger = (
    <div
      className={cn('studentAbsence', {
        studentAbsenceDisabled: disabled,
        dark: theme.mode === ThemeMode.Dark,
      })}
      tabIndex={disabled || isUpdating ? -1 : 0}
      role={disabled || isUpdating ? undefined : 'button'}
    >
      <div key="icon_wrapper" className="iconWrapper">
        {getIcon()}
      </div>
      <div key="student_name" className="studentName">
        <div key="name" className="name">
          {getSortedUserLabel(student, preferenceSorting ?? UserListSorting.FirstName)}
        </div>
        <div key="badges" className="tagWrapper">
          {getBadges()}
        </div>
      </div>
      <div key="icon_wrapper_2" className="buttonWrapper">
        {isUpdating ? <Loader size="sm" /> : <ChevronDownRegular />}
      </div>
    </div>
  );

  if (disabled || isUpdating) {
    return trigger;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>{trigger}</MenuTrigger>
      <MenuPopover>
        <MenuList>
          {menuOptions.map((option) => (
            <MenuItem key={option.key} onClick={() => handleMenuItemClick(option)}>
              {option.content}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
